import {
  hasListMarker,
  type MarkdownListKind,
  readListAttrs,
} from './list-attrs';
import type {
  Command,
  EditorState,
  PMNode,
  PMSelection,
  ResolvedPos,
} from './pm';
import {
  Fragment,
  findListsRange,
  isListNode,
  ReplaceStep,
  Slice,
  TextSelection,
} from './pm';

export type ListSortDirection = 'asc' | 'desc';

/**
 * The sibling list items a list operation covers.
 *
 * Shaped like the library's `NodeRange` but with an explicit item window:
 * a `NodeRange` derives its indices from `$from`/`$to` on every access, so a
 * caller could not narrow the window without also moving the selection it is
 * measured from. The resolved positions here are only used to address the
 * window's start and end in the document.
 */
export type ListIndexRange = {
  $from: ResolvedPos;
  parent: PMNode;
  depth: number;
  /** Marker of the first covered sibling; both marker tests measure against it. */
  listKind: MarkdownListKind;
  /** Index of the first sibling covered. */
  startIndex: number;
  /** Index after the last sibling covered (exclusive). */
  endIndex: number;
};

/**
 * The sibling items a selection touches, optionally widened to the neighbours
 * that share the first item's marker.
 *
 * The range itself comes from the list library, so a selection that starts and
 * ends at different nesting depths, runs backwards, or selects a whole node all
 * resolve to the same set of siblings. It guarantees siblings, but not one
 * marker: a selection can cross a bullet run and the ordered run next to it,
 * which Markdown writes as two lists.
 *
 * `widen` extends the span over the neighbours sharing that marker, because
 * Markdown writes a contiguous run as a single list, so half of one cannot
 * change without the other half following. Without it the range is exactly
 * what the selection touched, which is what a sort needs: unselected items
 * must stay where they are.
 */
export function findListIndexRange(
  selection: PMSelection,
  { widen }: { widen: boolean },
): ListIndexRange | null {
  const range = findListsRange(selection.$from, selection.$to);
  if (!range) return null;

  const { $from, parent, depth } = range;
  const listKind = readListAttrs(parent.child(range.startIndex))?.listKind;
  if (!listKind) return null;

  let startIndex = range.startIndex;
  let endIndex = range.endIndex;

  if (widen) {
    while (
      startIndex > 0 &&
      hasListMarker(parent.child(startIndex - 1), listKind)
    ) {
      startIndex--;
    }
    while (
      endIndex < parent.childCount &&
      hasListMarker(parent.child(endIndex), listKind)
    ) {
      endIndex++;
    }
  }

  if (endIndex <= startIndex) return null;

  return { $from, parent, depth, listKind, startIndex, endIndex };
}

/**
 * The selection a sort may act on, or null when it has nothing to reorder.
 *
 * A collapsed caret is deliberately not sortable: right-clicking an
 * unselected list must not offer to shuffle it. Two items are needed for a
 * reorder to mean anything, so a selection inside a single item is declined
 * the same way.
 *
 * The touched span is then cut at the first item that does not share the
 * leading marker. A selection can reach from a bullet run into the ordered run
 * beside it, and those are two Markdown lists, so reordering across them would
 * rewrite one of them as a side effect: the run the selection only grazed
 * keeps its order. Cutting here is what sorting specifically needs — a marker
 * change deliberately does the opposite and rewrites the whole touched span
 * into one uniform run.
 */
function sortableListRange(state: EditorState): ListIndexRange | null {
  if (state.selection.empty) return null;

  const range = findListIndexRange(state.selection, { widen: false });
  if (!range) return null;

  const { parent, startIndex, endIndex, listKind } = range;
  let end = endIndex;

  for (let index = startIndex + 1; index < endIndex; index++) {
    if (!hasListMarker(parent.child(index), listKind)) {
      end = index;
      break;
    }
  }

  if (end - startIndex < 2) return null;

  return { ...range, endIndex: end };
}

/** Whether `sortList` would reorder anything for this selection. */
export function canSortList(state: EditorState): boolean {
  return sortableListRange(state) !== null;
}

/**
 * Sort key for one item: its own text before its first nested list, with
 * whitespace collapsed. Nested items are their parent's content here, so they
 * travel with the parent instead of being ordered on their own.
 */
function listItemSortKey(node: PMNode): string {
  let text = '';

  for (let index = 0; index < node.childCount; index++) {
    const child = node.child(index);
    if (isListNode(child)) break;
    text += (text ? ' ' : '') + child.textContent;
  }

  return text.replace(/\s+/g, ' ').trim();
}

function compareSortKeys(
  left: string,
  right: string,
  direction: ListSortDirection,
): number {
  const result = left.localeCompare(right, undefined, {
    // "Item 2" sorts before "Item 10", and case or accents never decide an
    // order on their own.
    numeric: true,
    sensitivity: 'base',
  });

  return direction === 'desc' ? -result : result;
}

/**
 * `order` is an explicit start number for a run's head, so only the item that
 * ends up first may carry it. Markdown always writes `1.` and the CSS counter
 * restarts at the run's first item regardless, so the rest are cleared rather
 * than left behind mid-run.
 */
function withRunOrder(node: PMNode, order: number | null): PMNode {
  if (node.attrs.order === order) return node;

  return node.type.create({ ...node.attrs, order }, node.content, node.marks);
}

function sortedListItems(
  range: ListIndexRange,
  direction: ListSortDirection,
): PMNode[] {
  const { parent, startIndex, endIndex } = range;
  const items: PMNode[] = [];

  for (let index = startIndex; index < endIndex; index++) {
    items.push(parent.child(index));
  }

  const startOrder = readListAttrs(items[0])?.order ?? null;

  return (
    items
      .map((node, index) => ({ index, key: listItemSortKey(node), node }))
      // Ties keep the document's order, so the result never depends on the sort
      // implementation shuffling equal keys.
      .sort(
        (a, b) => compareSortKeys(a.key, b.key, direction) || a.index - b.index,
      )
      .map(({ node }, index) =>
        withRunOrder(node, index === 0 ? startOrder : null),
      )
  );
}

/**
 * Reorders the top-level items the selection touches, in place, as a single
 * replace step so undo sees one action.
 *
 * Applies to bullet, ordered and task lists alike: the items are the siblings
 * of the selection's level, each carrying its own nested children, marker and
 * checked state. The sorted run is left selected so the same selection can be
 * sorted again — or reversed — without reselecting by hand.
 */
export function sortList(direction: ListSortDirection): Command {
  return (state, dispatch) => {
    const range = sortableListRange(state);
    if (!range) return false;

    const { $from, depth, startIndex, endIndex } = range;
    const from = $from.posAtIndex(startIndex, depth);
    const to = $from.posAtIndex(endIndex, depth);
    const sorted = Fragment.fromArray(sortedListItems(range, direction));

    const step = new ReplaceStep(from, to, new Slice(sorted, 0, 0));
    if (step.apply(state.doc).failed) return false;
    if (!dispatch) return true;

    const tr = state.tr.step(step);
    const sortedEnd = from + sorted.size;
    const firstText = TextSelection.near(tr.doc.resolve(from), 1);
    const lastText = TextSelection.near(tr.doc.resolve(sortedEnd), -1);

    if (
      firstText instanceof TextSelection &&
      lastText instanceof TextSelection
    ) {
      tr.setSelection(
        TextSelection.create(tr.doc, firstText.from, lastText.to),
      );
    }

    dispatch(tr.scrollIntoView());
    return true;
  };
}
